import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { payrollApi } from '../../services/api';
import type { CurrentPayRate, PayrollRateChange, PayrollSlot, ShiftType } from '../../types';
import { currentMonthKey, formatDateKey, formatMoneyInput, formatVnd, parseMoneyInput } from '../../utils/money';
import { centerDateKey } from '../../utils/geolocation';
import { Button, DateInput, Input, LoadingState, Modal, Select, StatusBadge, useToast } from '../ui';

export interface RatePerson {
    id: string;
    fullName: string;
}

const slotLabels: Record<PayrollSlot, string> = { TEACHER: 'Giảng dạy', ASSISTANT: 'Trợ giảng' };
const SLOTS: PayrollSlot[] = ['TEACHER', 'ASSISTANT'];

// The change in effect today for a slot and shift type, if any.
export function currentChange(changes: PayrollRateChange[], slot: PayrollSlot, shiftTypeId: string, today = centerDateKey()) {
    return changes
        .filter(change => change.slot === slot && change.shiftTypeId === shiftTypeId && change.effectiveFrom <= today)
        .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0] ?? null;
}

// Today's pay per shift type, in the shape the profile and teacher views read.
export function currentPayRates(changes: PayrollRateChange[], types: ShiftType[]): CurrentPayRate[] {
    return types
        .map(type => ({
            shiftTypeId: type.id,
            name: type.name,
            minutes: type.minutes,
            teacher: currentChange(changes, 'TEACHER', type.id)?.rate ?? null,
            assistant: currentChange(changes, 'ASSISTANT', type.id)?.rate ?? null,
        }))
        .filter((item, index) => types[index].isActive || item.teacher !== null || item.assistant !== null);
}

interface RateHistoryDialogProps {
    person: RatePerson | null;
    onClose: () => void;
    onSaved?: (rates: { payRates: CurrentPayRate[] }) => void;
}

// Pay per shift, by shift type: each rate applies from its date until the next one, so raises never change past months.
export default function RateHistoryDialog({ person, onClose, onSaved }: RateHistoryDialogProps) {
    const { showToast } = useToast();
    const [changes, setChanges] = useState<PayrollRateChange[] | null>(null);
    const [types, setTypes] = useState<ShiftType[]>([]);
    const [slot, setSlot] = useState<PayrollSlot>('TEACHER');
    const [shiftTypeId, setShiftTypeId] = useState('');
    const [effectiveFrom, setEffectiveFrom] = useState(`${currentMonthKey()}-01`);
    const [rate, setRate] = useState('');
    const [saving, setSaving] = useState(false);
    const [confirmingId, setConfirmingId] = useState<string | null>(null);

    useEffect(() => {
        if (!person) return;
        setChanges(null);
        setRate('');
        setConfirmingId(null);
        setEffectiveFrom(`${currentMonthKey()}-01`);
        Promise.all([payrollApi.getRateChanges(person.id), payrollApi.getShiftTypes()])
            .then(([changesResponse, typesResponse]) => {
                setTypes(typesResponse.data.data);
                setShiftTypeId(current => current || typesResponse.data.data.find(type => type.isActive)?.id || '');
                setChanges(changesResponse.data.data);
            })
            .catch((error: any) => {
                showToast(error?.message || 'Không tải được lịch sử lương.', 'error');
                setChanges([]);
            });
    }, [person, showToast]);

    // Active types, plus retired ones the person still has history for.
    const shownTypes = useMemo(
        () => types.filter(type => type.isActive || changes?.some(change => change.shiftTypeId === type.id)),
        [types, changes],
    );

    if (!person) return null;

    const applyResult = (next: PayrollRateChange[]) => {
        setChanges(next);
        onSaved?.({ payRates: currentPayRates(next, types) });
    };

    const typeName = (id: string) => types.find(type => type.id === id)?.name ?? 'loại ca';

    const add = async (event: React.FormEvent) => {
        event.preventDefault();
        const amount = parseMoneyInput(rate);
        if (!shiftTypeId || !effectiveFrom || amount === null) {
            showToast('Chọn loại ca, ngày áp dụng và nhập lương/ca.', 'error');
            return;
        }
        setSaving(true);
        try {
            const response = await payrollApi.addRateChange(person.id, { slot, shiftTypeId, effectiveFrom, rate: amount });
            applyResult(response.data.data);
            setRate('');
            showToast(`Đã lưu lương ${slotLabels[slot].toLowerCase()} ${typeName(shiftTypeId)} từ ${formatDateKey(effectiveFrom)}.`, 'success');
        } catch (error: any) {
            showToast(error?.message || 'Lưu mức lương thất bại.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (change: PayrollRateChange) => {
        setSaving(true);
        try {
            const response = await payrollApi.deleteRateChange(person.id, change.id);
            applyResult(response.data.data);
            setConfirmingId(null);
            showToast('Đã xoá mức lương.', 'success');
        } catch (error: any) {
            showToast(error?.message || 'Xoá mức lương thất bại.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const replacing = changes?.some(change => change.slot === slot && change.shiftTypeId === shiftTypeId && change.effectiveFrom === effectiveFrom);

    return (
        <Modal
            open
            title="Lương theo ca"
            description={`${person.fullName}. Mỗi ca tính theo mức của loại ca đó đang áp dụng vào ngày của ca.`}
            onClose={() => !saving && onClose()}
            className="sm:max-w-2xl"
            footer={(
                <div className="flex justify-end">
                    <Button variant="secondary" onClick={onClose} disabled={saving}>Đóng</Button>
                </div>
            )}
        >
            {!changes ? (
                <LoadingState title="Đang tải lịch sử lương" />
            ) : (
                <div className="space-y-5">
                    {shownTypes.length === 0 ? (
                        <p className="rounded-lg border border-dashed border-ink-200 px-3 py-2 text-sm text-ink-500">
                            Chưa có loại ca nào. Thêm loại ca ở trang Chấm công.
                        </p>
                    ) : shownTypes.map(type => (
                        <section key={type.id} className="space-y-2" aria-label={type.name}>
                            <h3 className="text-sm font-semibold text-ink-900">
                                {type.name}
                                {!type.isActive ? <span className="ml-2 text-xs font-normal text-ink-500">(ngừng dùng)</span> : null}
                            </h3>
                            <div className="grid gap-3 sm:grid-cols-2">
                                {SLOTS.map(item => {
                                    const rows = changes
                                        .filter(change => change.slot === item && change.shiftTypeId === type.id)
                                        .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
                                    const current = currentChange(changes, item, type.id);
                                    return (
                                        <div key={item} className="space-y-1.5">
                                            <p className="text-xs font-medium text-ink-600">{slotLabels[item]}</p>
                                            {rows.length === 0 ? (
                                                <p className="rounded-lg border border-dashed border-ink-200 px-3 py-2 text-sm text-ink-500">Chưa có mức lương</p>
                                            ) : (
                                                <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                                                    {rows.map(change => (
                                                        <li key={change.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                                                            <div className="min-w-0">
                                                                <p className="font-medium tabular-nums text-ink-900">{formatVnd(change.rate)}/ca</p>
                                                                <p className="text-xs text-ink-500">
                                                                    Từ {formatDateKey(change.effectiveFrom)}
                                                                    {change.effectiveFrom > centerDateKey() ? ' · sắp áp dụng' : ''}
                                                                </p>
                                                            </div>
                                                            <div className="flex shrink-0 items-center gap-1">
                                                                {current?.id === change.id ? <StatusBadge tone="success" icon={null}>Đang áp dụng</StatusBadge> : null}
                                                                {confirmingId === change.id ? (
                                                                    <>
                                                                        <Button size="sm" variant="danger" loading={saving} onClick={() => remove(change)}>Xoá</Button>
                                                                        <Button size="sm" variant="ghost" disabled={saving} onClick={() => setConfirmingId(null)}>Huỷ</Button>
                                                                    </>
                                                                ) : (
                                                                    <Button
                                                                        size="icon"
                                                                        variant="ghost"
                                                                        className="h-8 w-8"
                                                                        aria-label={`Xoá lương ${slotLabels[item].toLowerCase()} ${type.name} từ ${formatDateKey(change.effectiveFrom)}`}
                                                                        icon={<Trash2 className="h-4 w-4" />}
                                                                        onClick={() => setConfirmingId(change.id)}
                                                                    />
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ))}

                    <form onSubmit={add} className="space-y-3 rounded-lg border border-ink-200 bg-ink-50 p-4">
                        <p className="text-sm font-semibold text-ink-900">Thêm mức lương mới</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Select label="Vai trò" value={slot} onChange={event => setSlot(event.target.value as PayrollSlot)} disabled={saving}>
                                <option value="TEACHER">Giảng dạy</option>
                                <option value="ASSISTANT">Trợ giảng</option>
                            </Select>
                            <Select label="Loại ca" value={shiftTypeId} onChange={event => setShiftTypeId(event.target.value)} disabled={saving}>
                                {types.filter(type => type.isActive).map(type => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                ))}
                            </Select>
                            <DateInput label="Áp dụng từ" value={effectiveFrom} onChange={setEffectiveFrom} clearable={false} disabled={saving} />
                            <Input
                                label="Lương/ca (₫)"
                                inputMode="numeric"
                                placeholder="400.000"
                                value={rate}
                                onChange={event => setRate(formatMoneyInput(parseMoneyInput(event.target.value)))}
                                disabled={saving}
                            />
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-ink-500">
                                {replacing
                                    ? `Ngày ${formatDateKey(effectiveFrom)} đã có mức ${slotLabels[slot].toLowerCase()} ${typeName(shiftTypeId)}; lưu sẽ thay mức đó.`
                                    : 'Mức mới áp dụng từ ngày đã chọn cho đến khi có mức tiếp theo. Các ca trước ngày đó giữ nguyên mức cũ.'}
                            </p>
                            <Button type="submit" loading={saving} icon={<Plus className="h-4 w-4" />} className="shrink-0">
                                {replacing ? 'Cập nhật' : 'Thêm'}
                            </Button>
                        </div>
                    </form>
                </div>
            )}
        </Modal>
    );
}
